import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import { Text } from '@revelio/ui';

import { dashMetaFetch } from '../../../../shared/api/dash-meta-fetch';
import { TanstackQueryError } from '../../../../shared/data-fetch/tanstack-query-provider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { useRoleLeafIds } from '../../filters/hooks/useRoleLeafIds';
import type { PrimaryFilterId } from '../../filters/types/filters';
import {
  generateSentimentPayload,
  MissingRoleLeafIdsError,
} from '../../filters/utils/generateSentimentReviewPayload';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import { useSelectedPrimaryEntity } from '../../hooks/useSelectedPrimaryEntity';
import { isSentimentSnapshotPage } from '../DataProvider/utils/sentiment-snapshot-page';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { usePageState } from '../PageState/PageStateProvider';
import { ChartError } from './ChartError';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import {
  isChartLoadingFromSwitcher,
  useClearChartSwitcherPlotWhenDone,
} from './hooks/useChartSwitcherPlot';
import styles from './SentimentReview.module.css';

type SentimentProps = {
  primaryDimension: PrimaryFilterId;
};

export const SentimentReview = ({ primaryDimension }: SentimentProps) => {
  const primaryEntity = useSelectedPrimaryEntity();
  const metric = usePlotMetric();

  const effectiveFilters = useEffectiveFilters();

  const { config } = useTabContentConfig();
  const isSnapshotDate = isSentimentSnapshotPage(config.id);

  const roleLeafIds = useRoleLeafIds(effectiveFilters);

  const payloadResult = (() => {
    if (!primaryEntity) return null;

    return generateSentimentPayload({
      filters: effectiveFilters,
      roleLeafIds,
      primaryFilter: primaryDimension,
      reviewType: metric === 'negative_review' ? 'neg' : 'pos',
      selectedEntity: {
        id: Number(primaryEntity.id),
        label: primaryEntity.name,
        selectionListId: primaryEntity.levelId,
      },
      isSnapshotDate,
    });
  })();
  const payload =
    payloadResult instanceof MissingRoleLeafIdsError ? null : payloadResult;

  const { data, isLoading } = useQuery({
    queryKey: ['sentiment-review', payload],
    queryFn: async () => {
      if (!payload) return null;
      const requestID = crypto.randomUUID();

      const response = await dashMetaFetch('/api/sentiment/reviews', {
        method: 'POST',
        headers: { 'request-id': requestID },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new TanstackQueryError(
          'Failed to fetch sentiment reviews',
          requestID
        );
      }

      return response.json();
    },
    enabled: !!payload,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  useClearChartSwitcherPlotWhenDone(!isLoading);

  const { item } = useChart();
  const { pageState } = usePageState();
  const cardScopedLoader = isChartLoadingFromSwitcher(
    item.id,
    pageState.chartSwitcherPlotId
  );

  if (payloadResult instanceof MissingRoleLeafIdsError) {
    return <ChartError message={payloadResult.message} />;
  }

  return (
    <div
      className={classNames(
        styles.reviewRoot,
        cardScopedLoader && styles.reviewRootScoped
      )}
    >
      {isLoading && <ChartLoader />}
      <div className={styles.reviewScroll}>
        <div className={styles.reviewRow}>
          <Text fontSize="sm" className={styles.reviewText}>
            {data?.review || data?.error || ''}
          </Text>
        </div>
      </div>
    </div>
  );
};
